import type { CatalogItem, Category, ContentKind, ItemQuery, Page, SearchHit, SearchQuery, SeriesDetails, Source, SyncProgress } from '@viva/core';
import type { RpcClient } from '../rpc/client';
import type { CallContext } from '../rpc/protocol';
import type { CatalogApi, NewSourceInput } from './catalog-service';

/** Typed proxy for a `CatalogService` exposed with `exposeApi`. */
export class CatalogClient implements CatalogApi {
  constructor(private readonly rpc: RpcClient) {}

  listSources(): Promise<Source[]> {
    return this.rpc.call('listSources', []);
  }

  addSource(input: NewSourceInput, context?: CallContext<SyncProgress>): Promise<Source> {
    return this.rpc.call('addSource', [input], context as CallContext);
  }

  refreshSource(id: string, context?: CallContext<SyncProgress>): Promise<Source> {
    return this.rpc.call('refreshSource', [id], context as CallContext);
  }

  reimportFile(id: string, file: Blob, context?: CallContext<SyncProgress>): Promise<Source> {
    return this.rpc.call('reimportFile', [id, file], context as CallContext);
  }

  removeSource(id: string): Promise<void> {
    return this.rpc.call('removeSource', [id]);
  }

  listCategories(sourceId: string, kind: ContentKind): Promise<Category[]> {
    return this.rpc.call('listCategories', [sourceId, kind]);
  }

  listItems(query: ItemQuery): Promise<Page<CatalogItem>> {
    return this.rpc.call('listItems', [query]);
  }

  listRecent(sourceId: string, kind: ContentKind, limit: number): Promise<CatalogItem[]> {
    return this.rpc.call('listRecent', [sourceId, kind, limit]);
  }

  search(query: SearchQuery): Promise<SearchHit[]> {
    return this.rpc.call('search', [query]);
  }

  getSeriesDetails(sourceId: string, itemKey: string, context?: CallContext): Promise<SeriesDetails> {
    return this.rpc.call('getSeriesDetails', [sourceId, itemKey], context);
  }
}
